import React, { useEffect, useMemo, useState } from 'react';
import { FaExclamationTriangle, FaLock, FaRedo, FaShieldAlt } from 'react-icons/fa';
import { supabase } from '../lib/supabaseClient';

type AuditSummary = {
  ok: boolean;
  generated_at?: string;
  totals?: { events_24h: number; critical_24h: number; warnings_24h: number };
  checks?: Array<{ key: string; label: string; status: 'ok' | 'warning' | 'critical'; detail: string }>;
  recent_events?: Array<any>;
  error?: string;
};

export default function AdminSecurityCenter() {
  const [data, setData] = useState<AuditSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: result, error: fnError } = await supabase.functions.invoke('security-audit', {
        body: { mode: 'admin_summary' },
      });
      if (fnError) throw fnError;
      setData(result);
    } catch (e: any) {
      setError(e?.message || 'Impossible de charger le centre sécurité.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const checks = useMemo(() => data?.checks || [], [data]);

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-8 text-white sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 shadow-2xl backdrop-blur">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-black uppercase tracking-[0.25em] text-emerald-200">
                <FaShieldAlt /> LOT 50
              </div>
              <h1 className="mt-4 text-3xl font-black tracking-tight md:text-5xl">Centre sécurité Forfeo</h1>
              <p className="mt-3 max-w-3xl text-slate-300">Suivi des événements de sécurité, durcissement des fonctions, contrôles d’intégrité et préparation production.</p>
            </div>
            <button onClick={load} className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3 font-black text-slate-950 shadow-xl hover:bg-emerald-50" disabled={loading}>
              <FaRedo className={loading ? 'animate-spin' : ''} /> Rafraîchir
            </button>
          </div>
        </div>

        {error && <div className="rounded-2xl border border-red-400/30 bg-red-500/10 p-4 text-red-100">{error}</div>}

        <div className="grid gap-4 md:grid-cols-3">
          <Stat label="Événements 24h" value={data?.totals?.events_24h ?? 0} />
          <Stat label="Avertissements 24h" value={data?.totals?.warnings_24h ?? 0} />
          <Stat label="Critiques 24h" value={data?.totals?.critical_24h ?? 0} danger />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {checks.map((check) => (
            <div key={check.key} className="rounded-3xl border border-white/10 bg-white/[0.04] p-5">
              <div className="flex items-start gap-3">
                <div className={`rounded-2xl p-3 ${check.status === 'ok' ? 'bg-emerald-400/15 text-emerald-200' : check.status === 'warning' ? 'bg-amber-400/15 text-amber-200' : 'bg-red-400/15 text-red-200'}`}>
                  {check.status === 'ok' ? <FaLock /> : <FaExclamationTriangle />}
                </div>
                <div>
                  <h3 className="font-black">{check.label}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-300">{check.detail}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-5">
          <h2 className="text-xl font-black">Événements récents</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="text-slate-400"><tr><th className="py-2">Date</th><th>Type</th><th>Sévérité</th><th>Page</th></tr></thead>
              <tbody>
                {(data?.recent_events || []).map((event, idx) => (
                  <tr key={event.id || idx} className="border-t border-white/10">
                    <td className="py-3 text-slate-300">{event.created_at ? new Date(event.created_at).toLocaleString('fr-CA') : '-'}</td>
                    <td className="font-bold">{event.event_type}</td>
                    <td>{event.severity || 'info'}</td>
                    <td className="max-w-[360px] truncate text-slate-400">{event.page_url || '-'}</td>
                  </tr>
                ))}
                {(!data?.recent_events || data.recent_events.length === 0) && <tr><td className="py-6 text-slate-400" colSpan={4}>Aucun événement récent.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, danger = false }: { label: string; value: number; danger?: boolean }) {
  return <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-5"><div className="text-sm text-slate-400">{label}</div><div className={`mt-2 text-4xl font-black ${danger && value > 0 ? 'text-red-300' : 'text-white'}`}>{value}</div></div>;
}
